import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { Issue, Person } from '../game/types';
import { TYPE_LABEL, shortDate } from '../game/format';

interface Props {
  issue: Issue;
  assignee: Person | undefined;
  statusName: string;
  flagged: boolean;
  onToggleFlag: () => void;
  onClose: () => void;
}

/**
 * Full issue view. Corroboration only -- by content rule, nothing in here is
 * required to spot a defect, because forcing a drill-down on every card would
 * make the phone experience miserable.
 */
export function DetailSheet({
  issue,
  assignee,
  statusName,
  flagged,
  onToggleFlag,
  onClose,
}: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rows: Array<[string, ReactNode]> = [
    ['Type', TYPE_LABEL[issue.type] ?? issue.type],
    ['Status', statusName],
    [
      'Assignee',
      assignee ? (
        <>
          {assignee.name}
          {assignee.inactive && (
            <span style={{ color: 'var(--red)' }}> · account deactivated</span>
          )}
        </>
      ) : (
        <span style={{ color: 'var(--text-subtle)' }}>Unassigned</span>
      ),
    ],
    ['Priority', issue.priority],
    [
      'Estimate',
      issue.points !== undefined
        ? `${issue.points} points`
        : issue.estimateHours !== undefined
          ? `${issue.estimateHours} hours`
          : 'None',
    ],
    ['Sprint', issue.sprint ?? 'Not in a sprint'],
  ];

  if (issue.parentKey) rows.push(['Parent', issue.parentKey]);
  if (issue.blockedBy?.length) rows.push(['Blocked by', issue.blockedBy.join(', ')]);
  if (issue.startDate) rows.push(['Start date', shortDate(issue.startDate)]);
  if (issue.dueDate) rows.push(['Due date', shortDate(issue.dueDate)]);
  rows.push(['Linked MR', issue.linkedPr ?? 'None']);
  rows.push(['Acceptance criteria', issue.acceptanceCriteria ?? 0]);
  if (issue.idleDays !== undefined) rows.push(['Last updated', `${issue.idleDays} days ago`]);
  if (issue.labels?.length) rows.push(['Labels', issue.labels.join(', ')]);

  return (
    <div
      className="scrim"
      role="dialog"
      aria-modal="true"
      aria-label={`Details for ${issue.key}`}
      onClick={onClose}
    >
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__grip" aria-hidden="true" />

        <div className="sheet__head">
          <span className="card__key">{issue.key}</span>
        </div>
        <h2 className="sheet__title">{issue.summary}</h2>

        <dl className="fields">
          {rows.map(([label, value]) => (
            <div key={label} style={{ display: 'contents' }}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>

        <div className="sheet__actions">
          <button type="button" className="btn" onClick={onToggleFlag}>
            {flagged ? 'Remove flag' : 'Flag this issue'}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            ref={closeRef}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
